/**
 * Portal sessions: shared types and formatting for My preferences → Sessions and
 * the Team drawer's Sign-in tab (routers/admin_sessions.py).
 */

export interface AdminSession {
  id: string;
  created_at: string | null;
  last_activity_at: string | null;
  expires_at: string | null;
  ip_address: string | null;
  device: string;
  user_agent: string | null;
  current: boolean;
}

export interface SessionsResponse {
  sessions: AdminSession[];
  max_hours: number;
}

export interface SessionSummary {
  active_sessions: number;
  last_active_at: string | null;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now", "12 min ago", "3 h ago", "2 d ago". Empty for a missing or bad date. */
export function relativeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, now - t);
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} h ago`;
  return `${Math.floor(diff / DAY)} d ago`;
}

/**
 * When the session actually ends: the earlier of its issued expiry and sign-in
 * plus the Settings → Security limit (the same rule the server applies).
 */
export function sessionEndsAt(s: AdminSession, maxHours: number): Date | null {
  const candidates: number[] = [];
  const expires = s.expires_at ? Date.parse(s.expires_at) : NaN;
  if (!Number.isNaN(expires)) candidates.push(expires);
  const created = s.created_at ? Date.parse(s.created_at) : NaN;
  if (!Number.isNaN(created) && maxHours > 0) candidates.push(created + maxHours * HOUR);
  return candidates.length ? new Date(Math.min(...candidates)) : null;
}

/** Second line under the device name: where from, when signed in, last active. */
export function sessionSubtitle(s: AdminSession, now: number = Date.now()): string {
  const parts: string[] = [];
  if (s.ip_address) parts.push(s.ip_address);
  const signedIn = relativeAgo(s.created_at, now);
  if (signedIn) parts.push(`signed in ${signedIn}`);
  const active = relativeAgo(s.last_activity_at, now);
  if (active && !s.current) parts.push(`last active ${active}`);
  return parts.join(' · ');
}

export function otherSessionCount(sessions: AdminSession[]): number {
  return sessions.filter(s => !s.current).length;
}

/** Team list cell: "2 open · active 5 min ago", "None open · active 3 d ago", "Never". */
export function summaryLabel(summary: SessionSummary | undefined, now: number = Date.now()): string {
  if (!summary) return 'Never';
  const open = summary.active_sessions > 0 ? `${summary.active_sessions} open` : 'None open';
  const ago = relativeAgo(summary.last_active_at, now);
  return ago ? `${open} · active ${ago}` : open;
}
